/**
 * Viewport Height Fix for Mobile Browsers
 * Fixes the 100vh issue on mobile devices where address bars affect height
 * Compatible avec tous les navigateurs mobiles (iOS Safari, Android Chrome, etc.)
 */

// Fonction pour définir la hauteur du viewport personnalisée
function setViewportHeight() {
  // Calculer 1% de la hauteur réelle du viewport
  const vh = window.innerHeight * 0.01;
  
  // Définir la variable CSS --vh
  document.documentElement.style.setProperty('--vh', `${vh}px`);
  
  // Définir également la variable --viewport-height pour une utilisation directe
  document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
}

// Fonction pour détecter le type de navigateur
function detectBrowser() {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  
  // Détection iOS
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    document.documentElement.classList.add('is-ios');
  }
  
  // Détection Android
  if (/android/i.test(userAgent)) {
    document.documentElement.classList.add('is-android');
  }
  
  // Détection Safari
  if (/^((?!chrome|android).)*safari/i.test(userAgent)) {
    document.documentElement.classList.add('is-safari');
  }
  
  // Détection Chrome
  if (/chrome/i.test(userAgent) && !/edge/i.test(userAgent)) {
    document.documentElement.classList.add('is-chrome');
  }
  
  // Détection Firefox
  if (/firefox/i.test(userAgent)) {
    document.documentElement.classList.add('is-firefox');
  }
  
  // Détection Opera
  if (/opr\//i.test(userAgent) || /opera/i.test(userAgent)) {
    document.documentElement.classList.add('is-opera');
  }
  
  // Détection Edge
  if (/edg/i.test(userAgent)) {
    document.documentElement.classList.add('is-edge');
  }
  
  // Détection Samsung Internet
  if (/samsungbrowser/i.test(userAgent)) {
    document.documentElement.classList.add('is-samsung');
  }
  
  // Détection device type
  if (/mobile/i.test(userAgent)) {
    document.documentElement.classList.add('is-mobile');
  }
  
  if (/tablet|ipad/i.test(userAgent)) {
    document.documentElement.classList.add('is-tablet');
  }
}

// Fonction pour détecter le support de certaines features
function detectFeatures() {
  // Touch support
  if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
    document.documentElement.classList.add('touch-enabled');
  } else {
    document.documentElement.classList.add('no-touch');
  }
  
  // Hover support
  if (window.matchMedia('(hover: hover)').matches) {
    document.documentElement.classList.add('hover-enabled');
  } else {
    document.documentElement.classList.add('no-hover');
  }
  
  // Pointer type
  if (window.matchMedia('(pointer: coarse)').matches) {
    document.documentElement.classList.add('pointer-coarse');
  } else if (window.matchMedia('(pointer: fine)').matches) {
    document.documentElement.classList.add('pointer-fine');
  }
  
  // Support pour backdrop-filter
  if (CSS.supports('backdrop-filter', 'blur(1px)') || 
      CSS.supports('-webkit-backdrop-filter', 'blur(1px)')) {
    document.documentElement.classList.add('backdrop-filter-supported');
  }
  
  // Support pour grid
  if (CSS.supports('display', 'grid')) {
    document.documentElement.classList.add('grid-supported');
  }
  
  // Support pour flexbox
  if (CSS.supports('display', 'flex')) {
    document.documentElement.classList.add('flex-supported');
  }
}

// Fonction pour gérer l'orientation
function handleOrientationChange() {
  const orientation = window.screen.orientation || {};
  const type = orientation.type || '';
  
  // Supprimer les classes d'orientation existantes
  document.documentElement.classList.remove('portrait', 'landscape');
  
  // Ajouter la classe appropriée
  if (type.includes('portrait') || window.innerHeight > window.innerWidth) {
    document.documentElement.classList.add('portrait');
  } else {
    document.documentElement.classList.add('landscape');
  }
}

// Fonction pour empêcher le zoom double-tap sur iOS
function preventDoubleTapZoom() {
  let lastTouchEnd = 0;
  
  document.addEventListener('touchend', function (event) {
    const now = Date.now();
    if (now - lastTouchEnd <= 300) {
      event.preventDefault();
    }
    lastTouchEnd = now;
  }, { passive: false });
}

// Fonction pour gérer le resize avec debounce
let resizeTimeout;
function handleResize() {
  clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(() => {
    setViewportHeight();
    handleOrientationChange();
  }, 100);
}

// Initialisation
export function initViewportFixes() {
  // Détecter le navigateur et les features
  detectBrowser();
  detectFeatures();
  
  // Définir la hauteur initiale
  setViewportHeight();
  
  // Gérer l'orientation initiale
  handleOrientationChange();
  
  // Empêcher le zoom double-tap sur iOS
  if (document.documentElement.classList.contains('is-ios')) {
    preventDoubleTapZoom();
  }
  
  // Écouter les événements de resize
  window.addEventListener('resize', handleResize, { passive: true });
  
  // Écouter les changements d'orientation
  if (window.screen && window.screen.orientation) {
    window.screen.orientation.addEventListener('change', handleOrientationChange);
  } else {
    window.addEventListener('orientationchange', handleOrientationChange);
  }
  
  // Re-calculer lors du load complet
  window.addEventListener('load', setViewportHeight);
  
  // Support pour le mode picture-in-picture et fullscreen
  document.addEventListener('fullscreenchange', setViewportHeight);
  document.addEventListener('webkitfullscreenchange', setViewportHeight);
  
  console.log('✅ Viewport fixes initialisés - Compatible tous navigateurs et appareils');
}

// Cleanup function
export function cleanupViewportFixes() {
  window.removeEventListener('resize', handleResize);
  window.removeEventListener('load', setViewportHeight);
  document.removeEventListener('fullscreenchange', setViewportHeight);
  document.removeEventListener('webkitfullscreenchange', setViewportHeight);
  
  if (window.screen && window.screen.orientation) {
    window.screen.orientation.removeEventListener('change', handleOrientationChange);
  } else {
    window.removeEventListener('orientationchange', handleOrientationChange);
  }
}

// Export default
const viewportFix = {
  init: initViewportFixes,
  cleanup: cleanupViewportFixes,
  setViewportHeight,
  detectBrowser,
  detectFeatures,
};

export default viewportFix;
